/**
 * @copyright 2026 Fortee Uniforme Inc. All rights reserved.
 * @license PROPRIETARY - Unauthorized copying prohibited.
 * Protected under the Canadian Copyright Act (R.S.C., 1985, c. C-42).
 */

// fortee-bg-retention.js - quick 260819-bb8. Scores how much real logo content a
// background-removal result destroyed, so a mangled logo can never reach a customer.
//
// WHY THIS EXISTS. A customer uploaded multi-element artwork (shop name, a
// transmission render, speed streaks, a phone number) on flat black. The removal
// engine is doing SALIENT-OBJECT ISOLATION, not background removal: it locked onto
// the single biggest object and deleted everything else. Measured on the committed
// fixture: 51.64% of all non-background content destroyed, silently, with the
// customer told "background removed". This module is the measurement that turns
// that into a rejection.
//
// Every function takes explicit (width, height, rgbaUint8Array) - never a canvas,
// never an Image - so the SAME code runs in the browser and under plain `node`.
// That is not tidiness: it is the only reason the 51.6% number can be pinned to a
// fixture in CI (scripts/test-bg-retention.cjs, npm run test:bg-retention).
//
// PURE: no Alpine, no DOM, no globals beyond this one namespace. Same boundary as
// assets/fortee-logo-placement.js, for the same reason.
//
// THE ONE SUBTLETY THAT MUST NOT BE SIMPLIFIED AWAY (see PLAN.md Risk 1):
// "content" here means "far from the border background colour". On a photographic
// or gradient background - precisely the case the paid AI exists for - nearly the
// whole frame is far from the border median, so a CORRECT removal scores as
// destroying most of the "content". Shipping the guard without tiering does not
// fix background removal, it disables it. Hence `valid` on scoreRetention and the
// background-independent `opaqueFraction` fallback in judgeCandidate.

(function () {
  'use strict';

  // A pixel is "content" when its max per-channel distance from the background
  // median exceeds this. 28 is the value the fixture measurements were taken at;
  // changing it invalidates the 586,801 / 303,005 gates in the test.
  var BORDER_CONTENT_TOL = 28;

  // Reject an AI result that destroyed more than this share of content.
  // Decision 3's threshold. Sits well below the 51.6% failure and above 0%.
  var RETENTION_REJECT = 0.15;

  // A content pixel is "destroyed" when the candidate's alpha falls under this.
  var DESTROYED_ALPHA = 32;
  // A pixel counts as "kept" / opaque above this.
  var OPAQUE_ALPHA = 200;

  // Tier thresholds.
  var FLAT_MAX_STD = 6;
  var FLAT_MATCH = 0.95;
  var SEMI_MATCH = 0.80;

  // Soft content ramp, for the union (see unionWithContent). A pixel this far
  // from the background reads as fully background; CONTENT_RAMP_HI and beyond
  // reads as fully artwork; in between the alpha ramps linearly. A hard
  // threshold at BORDER_CONTENT_TOL would restore text with jagged, aliased
  // edges - the ramp is what makes the restored letters look like letters.
  // Values ported unchanged from the verified prototype (buildGhost).
  var CONTENT_RAMP_LO = 10;
  var CONTENT_RAMP_HI = 34;

  // ---------------------------------------------------------------------------
  // Background profile
  // ---------------------------------------------------------------------------

  function median(values) {
    if (!values.length) return 0;
    var sorted = values.slice().sort(function (a, b) { return a - b; });
    return sorted[sorted.length >> 1];
  }

  function stdDev(values) {
    var n = values.length;
    if (!n) return 0;
    var sum = 0, i;
    for (i = 0; i < n; i++) sum += values[i];
    var mean = sum / n;
    var acc = 0;
    for (i = 0; i < n; i++) { var d = values[i] - mean; acc += d * d; }
    return Math.sqrt(acc / n);
  }

  // Walk the 1px frame ring exactly once per pixel. Degenerate 1xN / Nx1 frames
  // would otherwise double-count their corners.
  function forEachBorderPixel(w, h, fn) {
    var x, y;
    for (y = 0; y < h; y++) {
      if (y === 0 || y === h - 1) {
        for (x = 0; x < w; x++) fn(x, y);
      } else {
        fn(0, y);
        if (w > 1) fn(w - 1, y);
      }
    }
  }

  // Profile of the background, read from the 1px border ring only.
  //   r/g/b               per-channel MEDIAN of the ring (robust to a logo that
  //                       happens to touch an edge; a mean is not)
  //   maxStd              largest per-channel standard deviation over the ring
  //   borderMatchFraction share of ring pixels within BORDER_CONTENT_TOL of the median
  //   tier                'flat' | 'semi' | 'photo' - which removal strategy is safe
  function backgroundProfile(w, h, data) {
    w = w | 0; h = h | 0;
    if (w <= 0 || h <= 0 || !data) {
      return { r: 0, g: 0, b: 0, maxStd: 0, borderMatchFraction: 0, tier: 'photo' };
    }

    var rs = [], gs = [], bs = [];
    forEachBorderPixel(w, h, function (x, y) {
      var i = (y * w + x) * 4;
      rs.push(data[i]); gs.push(data[i + 1]); bs.push(data[i + 2]);
    });

    var mr = median(rs), mg = median(gs), mb = median(bs);
    var maxStd = Math.max(stdDev(rs), stdDev(gs), stdDev(bs));

    var match = 0;
    for (var k = 0; k < rs.length; k++) {
      var d = Math.max(
        Math.abs(rs[k] - mr),
        Math.abs(gs[k] - mg),
        Math.abs(bs[k] - mb)
      );
      if (d <= BORDER_CONTENT_TOL) match++;
    }
    var borderMatchFraction = rs.length ? (match / rs.length) : 0;

    var tier;
    if (maxStd <= FLAT_MAX_STD && borderMatchFraction >= FLAT_MATCH) tier = 'flat';
    else if (borderMatchFraction >= SEMI_MATCH) tier = 'semi';
    else tier = 'photo';

    return {
      r: mr, g: mg, b: mb,
      maxStd: maxStd,
      borderMatchFraction: borderMatchFraction,
      tier: tier
    };
  }

  // ---------------------------------------------------------------------------
  // Content mask
  // ---------------------------------------------------------------------------

  // Which pixels of the ORIGINAL are real artwork rather than background.
  // Deliberately ignores the source alpha channel: the original is by definition
  // the pre-removal upload, and an upload that already had alpha never reaches
  // here (the pipeline's fast path handles it).
  function contentMask(w, h, data, profile) {
    w = w | 0; h = h | 0;
    var n = Math.max(0, w * h);
    var mask = new Uint8Array(n);
    var count = 0;
    if (!data || !n) return { mask: mask, count: 0 };

    var p = profile || backgroundProfile(w, h, data);
    var pr = p.r, pg = p.g, pb = p.b;

    for (var i = 0; i < n; i++) {
      var j = i * 4;
      var d = Math.abs(data[j] - pr);
      var d2 = Math.abs(data[j + 1] - pg); if (d2 > d) d = d2;
      var d3 = Math.abs(data[j + 2] - pb); if (d3 > d) d = d3;
      if (d > BORDER_CONTENT_TOL) { mask[i] = 1; count++; }
    }
    return { mask: mask, count: count };
  }

  // The same measurement as contentMask, but as a SOFT alpha rather than a
  // boolean: distance CONTENT_RAMP_LO..CONTENT_RAMP_HI maps to alpha 0..255.
  // Returns a Uint8ClampedArray of w*h.
  //
  // This is the half of the union that restores what the AI deleted, so its
  // edges become the edges of the restored artwork. A hard cut at
  // BORDER_CONTENT_TOL leaves visibly staircased text; the anti-aliased pixels
  // of a real glyph live exactly inside this ramp.
  function contentAlpha(w, h, data, profile) {
    w = w | 0; h = h | 0;
    var n = Math.max(0, w * h);
    var out = new Uint8ClampedArray(n);
    if (!data || !n) return out;

    var p = profile || backgroundProfile(w, h, data);
    var pr = p.r, pg = p.g, pb = p.b;
    var span = CONTENT_RAMP_HI - CONTENT_RAMP_LO;

    for (var i = 0; i < n; i++) {
      var j = i * 4;
      var d = Math.abs(data[j] - pr);
      var d2 = Math.abs(data[j + 1] - pg); if (d2 > d) d = d2;
      var d3 = Math.abs(data[j + 2] - pb); if (d3 > d) d = d3;
      var t = (d - CONTENT_RAMP_LO) / span;
      out[i] = t <= 0 ? 0 : (t >= 1 ? 255 : t * 255);
    }
    return out;
  }

  // ---------------------------------------------------------------------------
  // Retention score
  // ---------------------------------------------------------------------------

  // Compare a removal candidate against the original.
  //
  // DIMENSION MISMATCH IS EXPECTED, not exceptional. The AI is under no obligation
  // to return at source resolution, and the pipeline loads both through a 1200px
  // cap that rounds independently. So the candidate is sampled by nearest
  // neighbour rather than by index parity - an off-by-one in the sizes would
  // otherwise silently score garbage and reject every result.
  //
  // Both arguments are { w, h, data } with data an RGBA byte array.
  function scoreRetention(orig, cand) {
    var empty = {
      contentPixels: 0, destroyedPixels: 0, destroyedFraction: 0,
      keptBackgroundPixels: 0, opaqueFraction: 0, tier: 'photo', valid: false
    };
    if (!orig || !cand || !orig.data || !cand.data) return empty;

    var ow = orig.w | 0, oh = orig.h | 0;
    var cw = cand.w | 0, ch = cand.h | 0;
    if (ow <= 0 || oh <= 0 || cw <= 0 || ch <= 0) return empty;

    var profile = backgroundProfile(ow, oh, orig.data);
    var cm = contentMask(ow, oh, orig.data, profile);

    var destroyed = 0;
    var keptBackground = 0;
    var x, y;
    for (y = 0; y < oh; y++) {
      var cy = Math.floor(y * ch / oh); if (cy >= ch) cy = ch - 1;
      var rowBase = y * ow;
      var candRow = cy * cw;
      for (x = 0; x < ow; x++) {
        var cx = Math.floor(x * cw / ow); if (cx >= cw) cx = cw - 1;
        var alpha = cand.data[(candRow + cx) * 4 + 3];
        if (cm.mask[rowBase + x]) {
          if (alpha < DESTROYED_ALPHA) destroyed++;
        } else if (alpha > OPAQUE_ALPHA) {
          keptBackground++;
        }
      }
    }

    // Share of the WHOLE candidate frame that is opaque. Computed on the
    // candidate's own grid and with no reference to the background colour, which
    // is exactly why it is the only signal that survives a 'photo' tier.
    var candPixels = cw * ch;
    var opaque = 0;
    for (var i = 0; i < candPixels; i++) {
      if (cand.data[i * 4 + 3] > OPAQUE_ALPHA) opaque++;
    }

    return {
      contentPixels: cm.count,
      destroyedPixels: destroyed,
      destroyedFraction: cm.count ? (destroyed / cm.count) : 0,
      keptBackgroundPixels: keptBackground,
      opaqueFraction: candPixels ? (opaque / candPixels) : 0,
      tier: profile.tier,
      // The retention metric is only meaningful when the border ring actually
      // describes a background. On 'photo' it does not, so destroyedFraction is
      // still REPORTED (it is useful in the benchmark) but must never drive a
      // rejection. See the header note and PLAN.md Risk 1.
      valid: profile.tier !== 'photo'
    };
  }

  // ---------------------------------------------------------------------------
  // Verdict
  // ---------------------------------------------------------------------------

  // The runtime guard's decision. Three independent ways a result can be wrong:
  //   destroyed content  - the bug this whole task exists for (51.6% on the fixture)
  //   erased everything  - the engine returned a near-empty frame
  //   no-op              - the engine returned the input, background intact
  // Everything else ships. A rejection is never destructive: the caller keeps the
  // customer's untouched original and flags it for a human.
  function judgeCandidate(orig, cand) {
    var s = scoreRetention(orig, cand);

    if (s.valid && s.destroyedFraction > RETENTION_REJECT) {
      return {
        accept: false,
        reason: 'bg-removal-destroyed-content',
        destroyedFraction: s.destroyedFraction
      };
    }
    if (s.opaqueFraction < 0.02) {
      return {
        accept: false,
        reason: 'bg-removal-erased-everything',
        destroyedFraction: s.destroyedFraction
      };
    }
    if (s.opaqueFraction > 0.99) {
      return {
        accept: false,
        reason: 'bg-removal-no-op',
        destroyedFraction: s.destroyedFraction
      };
    }
    return { accept: true, reason: '', destroyedFraction: s.destroyedFraction };
  }

  // ---------------------------------------------------------------------------
  // Union
  // ---------------------------------------------------------------------------

  // THE GATE. Do not inline this test anywhere else - it exists as one named,
  // asserted predicate precisely so it cannot be quietly deleted.
  //
  // The union is only valid when the border ring actually describes the
  // background. On a photographic background the content measurement marks
  // nearly the WHOLE frame as artwork, so unioning it back restores everything
  // and silently disables background removal. Measured on synthetic noise: a
  // CORRECT removal reads as destroyedFraction 0.8598, i.e. the union would put
  // 86% of the frame back. Ship the union ungated and background removal is
  // broken for every photo upload, invisibly.
  function canUnion(profile) {
    return !!profile && profile.tier !== 'photo';
  }

  // Combine a removal candidate's alpha with the soft content alpha of the
  // original, and return a new RGBA frame on the ORIGINAL's grid.
  //
  // WHY THE UNION IS THE RIGHT CUTOUT (measured on the Martel fixture):
  //   AI alone            27.1% opaque - text, phone and streaks all destroyed
  //   content mask alone  38.5% opaque - keeps every letter, holes the render's
  //                       own near-black shading
  //   union               47.0% opaque - correct, verified visually
  // Each half is wrong in a way the other half fixes. The AI keeps the subject
  // solid INCLUDING its own background-coloured shading, which no colour test
  // can do; the content measurement keeps every disjoint element the AI throws
  // away. Neither is shippable alone.
  //
  // RGB COMES FROM THE ORIGINAL, ALWAYS. Removal engines commonly zero or
  // premultiply the RGB of pixels they clear, so raising the candidate's alpha
  // on the candidate's own pixels would restore black rectangles rather than
  // artwork. Taking colour from the customer's file and only the alpha from the
  // engine is what the prototype proved out, and it is also why the result lands
  // at the original's resolution - the print resolution - rather than the
  // engine's.
  //
  // The candidate is sampled by nearest neighbour, same as scoreRetention: the
  // engine is under no obligation to return at source resolution.
  //
  // Returns { applied, reason, tier, w, h, data, restoredPixels }. `data` is
  // null whenever `applied` is false; the caller must then keep the raw
  // candidate rather than inventing one.
  function unionWithContent(orig, cand) {
    var skip = function (reason, tier) {
      return {
        applied: false, reason: reason, tier: tier || 'photo',
        w: 0, h: 0, data: null, restoredPixels: 0
      };
    };
    if (!orig || !cand || !orig.data || !cand.data) return skip('unusable');

    var ow = orig.w | 0, oh = orig.h | 0;
    var cw = cand.w | 0, ch = cand.h | 0;
    if (ow <= 0 || oh <= 0 || cw <= 0 || ch <= 0) return skip('unusable');

    var profile = backgroundProfile(ow, oh, orig.data);
    if (!canUnion(profile)) return skip('photo-background', profile.tier);

    var soft = contentAlpha(ow, oh, orig.data, profile);
    var out = new Uint8ClampedArray(ow * oh * 4);
    var restored = 0;
    var x, y;

    for (y = 0; y < oh; y++) {
      var cy = Math.floor(y * ch / oh); if (cy >= ch) cy = ch - 1;
      var rowBase = y * ow;
      var candRow = cy * cw;
      for (x = 0; x < ow; x++) {
        var i = rowBase + x;
        var o = i * 4;
        var cx = Math.floor(x * cw / ow); if (cx >= cw) cx = cw - 1;
        var candAlpha = cand.data[(candRow + cx) * 4 + 3];
        var s = soft[i];
        out[o] = orig.data[o];
        out[o + 1] = orig.data[o + 1];
        out[o + 2] = orig.data[o + 2];
        if (s > candAlpha) { out[o + 3] = s; restored++; }
        else { out[o + 3] = candAlpha; }
      }
    }

    return {
      applied: true, reason: '', tier: profile.tier,
      w: ow, h: oh, data: out, restoredPixels: restored
    };
  }

  // ---------------------------------------------------------------------------
  // Assertions
  // ---------------------------------------------------------------------------

  /* build:strip-start */
  // NOT SHIPPED. scripts/build-protected.js removes everything between these two
  // markers and substitutes an empty __selfTest, so the export below stays valid.
  // If you move or rename this function, MOVE THE MARKERS WITH IT.
  //
  // Synthetic 32x32 frames only. The real fixture assertions need a PNG decoder
  // and live in scripts/test-bg-retention.cjs.
  function __selfTest() {
    var failures = [];
    var passed = 0;

    function ok(cond, label) {
      if (cond) { passed++; } else { failures.push(label); }
    }
    function near(a, b, tol, label) {
      var d = Math.abs(Number(a) - Number(b));
      ok(isFinite(d) && d <= tol, label + ' (got ' + a + ', want ' + b + ' +/-' + tol + ')');
    }
    function eqNum(a, b, label) {
      ok(a === b, label + ' (got ' + a + ', want ' + b + ')');
    }

    // -- helpers -------------------------------------------------------------
    function frame(w, h, fill) {
      var d = new Uint8Array(w * h * 4);
      for (var i = 0; i < w * h; i++) {
        d[i * 4] = fill[0]; d[i * 4 + 1] = fill[1];
        d[i * 4 + 2] = fill[2]; d[i * 4 + 3] = 255;
      }
      return { w: w, h: h, data: d };
    }
    function box(f, x0, y0, x1, y1, rgb, alpha) {
      for (var y = y0; y <= y1; y++) {
        for (var x = x0; x <= x1; x++) {
          var i = (y * f.w + x) * 4;
          f.data[i] = rgb[0]; f.data[i + 1] = rgb[1]; f.data[i + 2] = rgb[2];
          if (typeof alpha === 'number') f.data[i + 3] = alpha;
        }
      }
      return f;
    }
    function setAlphaAll(f, a) {
      for (var i = 0; i < f.w * f.h; i++) f.data[i * 4 + 3] = a;
      return f;
    }

    // -- flat tier -----------------------------------------------------------
    // White frame, black 16x16 mark. 256 content pixels out of 1024.
    var flat = box(frame(32, 32, [255, 255, 255]), 8, 8, 23, 23, [0, 0, 0]);
    var fp = backgroundProfile(32, 32, flat.data);
    ok(fp.tier === 'flat', 'flat tier detected (got ' + fp.tier + ')');
    ok(fp.r === 255 && fp.g === 255 && fp.b === 255, 'flat profile is the border colour');
    near(fp.maxStd, 0, 0.001, 'flat maxStd is zero');
    near(fp.borderMatchFraction, 1, 0.001, 'flat borderMatchFraction is 1');
    var fm = contentMask(32, 32, flat.data, fp);
    ok(fm.count === 256, 'contentMask counts the mark only (got ' + fm.count + ')');

    // The mark does not touch the border, so a mid-grey background profiles the
    // same way - proving the profile is not white-specific.
    var grey = box(frame(32, 32, [128, 128, 128]), 8, 8, 23, 23, [10, 200, 40]);
    ok(backgroundProfile(32, 32, grey.data).tier === 'flat', 'flat tier on a mid-grey background');

    // -- a perfect removal ----------------------------------------------------
    var good = box(setAlphaAll(box(frame(32, 32, [255, 255, 255]), 8, 8, 23, 23, [0, 0, 0]), 0),
      8, 8, 23, 23, [0, 0, 0], 255);
    var sGood = scoreRetention(flat, good);
    ok(sGood.contentPixels === 256, 'perfect removal: content counted');
    ok(sGood.destroyedPixels === 0, 'perfect removal destroys nothing');
    ok(sGood.valid === true, 'perfect removal on a flat background is scoreable');
    near(sGood.opaqueFraction, 256 / 1024, 0.001, 'perfect removal opaqueFraction');
    ok(sGood.keptBackgroundPixels === 0, 'perfect removal keeps no background');
    ok(judgeCandidate(flat, good).accept === true, 'perfect removal is accepted');

    // -- the bug, in miniature ------------------------------------------------
    // Keeps only the left half of the mark: 50% of content destroyed. This is the
    // Martel failure shape at 32x32.
    var chopped = setAlphaAll(box(frame(32, 32, [255, 255, 255]), 8, 8, 23, 23, [0, 0, 0]), 0);
    box(chopped, 8, 8, 15, 23, [0, 0, 0], 255);
    var sChop = scoreRetention(flat, chopped);
    near(sChop.destroyedFraction, 0.5, 0.001, 'salient-object chop scores 50% destroyed');
    var vChop = judgeCandidate(flat, chopped);
    ok(vChop.accept === false, 'a 50% chop is rejected');
    ok(vChop.reason === 'bg-removal-destroyed-content', 'chop reason is destroyed-content');

    // -- erased everything ----------------------------------------------------
    var erased = setAlphaAll(frame(32, 32, [255, 255, 255]), 0);
    var vErased = judgeCandidate(flat, erased);
    ok(vErased.accept === false, 'a fully erased result is rejected');
    // destroyedFraction is 1.0 here so destroyed-content wins the ordering; both
    // are rejections, which is what matters. Score it on a photo tier to reach
    // the erased branch specifically - see below.

    // -- changed nothing (no-op) ----------------------------------------------
    var noop = { w: 32, h: 32, data: flat.data.slice() };
    var sNoop = scoreRetention(flat, noop);
    ok(sNoop.destroyedFraction === 0, 'no-op destroys nothing');
    near(sNoop.opaqueFraction, 1, 0.001, 'no-op is fully opaque');
    var vNoop = judgeCandidate(flat, noop);
    ok(vNoop.accept === false, 'a no-op result is rejected');
    ok(vNoop.reason === 'bg-removal-no-op', 'no-op reason is no-op');

    // -- photo tier: THE RISK 1 GUARD ----------------------------------------
    // Deterministic pseudo-random RGB. The border ring is noise, so the profile
    // is meaningless and nearly every pixel reads as "content". A correct removal
    // on this input would score as destroying almost everything.
    var photo = frame(32, 32, [0, 0, 0]);
    var seed = 12345;
    for (var pi = 0; pi < 32 * 32; pi++) {
      seed = (seed * 1103515245 + 12345) & 0x7fffffff;
      photo.data[pi * 4] = seed & 0xff;
      photo.data[pi * 4 + 1] = (seed >> 8) & 0xff;
      photo.data[pi * 4 + 2] = (seed >> 16) & 0xff;
    }
    var pp = backgroundProfile(32, 32, photo.data);
    ok(pp.tier === 'photo', 'random noise profiles as photo (got ' + pp.tier + ')');

    // A plausible correct removal on that photo: keep a centred subject, drop the rest.
    var subject = { w: 32, h: 32, data: photo.data.slice() };
    setAlphaAll(subject, 0);
    box(subject, 10, 10, 21, 21, [90, 90, 90], 255);   // 144 px = 14% opaque
    var sPhoto = scoreRetention(photo, subject);
    ok(sPhoto.valid === false, 'photo tier is not scoreable by retention');
    ok(sPhoto.destroyedFraction > RETENTION_REJECT,
      'photo tier still REPORTS a high destroyedFraction (got ' + sPhoto.destroyedFraction + ')');
    var vPhoto = judgeCandidate(photo, subject);
    ok(vPhoto.accept === true,
      'RISK 1: a correct removal on a photographic background is ACCEPTED despite a high destroyedFraction');

    // ...and the background-independent fallback still catches the two failures
    // that do not need a background profile.
    var photoErased = { w: 32, h: 32, data: photo.data.slice() };
    setAlphaAll(photoErased, 0);
    var vPhotoErased = judgeCandidate(photo, photoErased);
    ok(vPhotoErased.accept === false, 'photo tier: erased result still rejected');
    ok(vPhotoErased.reason === 'bg-removal-erased-everything',
      'photo tier: reason is erased-everything (got ' + vPhotoErased.reason + ')');

    var photoNoop = { w: 32, h: 32, data: photo.data.slice() };
    var vPhotoNoop = judgeCandidate(photo, photoNoop);
    ok(vPhotoNoop.accept === false, 'photo tier: no-op still rejected');
    ok(vPhotoNoop.reason === 'bg-removal-no-op', 'photo tier: reason is no-op');

    // -- size mismatch --------------------------------------------------------
    // Half-resolution candidate of the same 32x32 flat frame. floor(x*16/32) maps
    // the mark's 8..23 onto 4..11 exactly, so a correct nearest-neighbour sampler
    // scores 0 destroyed. Index parity would score garbage.
    var half = frame(16, 16, [255, 255, 255]);
    setAlphaAll(half, 0);
    box(half, 4, 4, 11, 11, [0, 0, 0], 255);
    var sHalf = scoreRetention(flat, half);
    ok(sHalf.contentPixels === 256, 'size mismatch: content still measured on the original');
    ok(sHalf.destroyedPixels === 0,
      'size mismatch: nearest-neighbour sampling destroys nothing (got ' + sHalf.destroyedPixels + ')');
    near(sHalf.opaqueFraction, 64 / 256, 0.001, 'size mismatch: opaqueFraction is on the candidate grid');
    ok(judgeCandidate(flat, half).accept === true, 'size mismatch: a good half-res result is accepted');

    // Upscaled candidate, the other direction.
    var dbl = frame(64, 64, [255, 255, 255]);
    setAlphaAll(dbl, 0);
    box(dbl, 16, 16, 47, 47, [0, 0, 0], 255);
    ok(scoreRetention(flat, dbl).destroyedPixels === 0,
      'size mismatch: a 2x candidate also destroys nothing');

    // -- the soft content ramp ------------------------------------------------
    // Distance 10 -> alpha 0, distance 34 -> alpha 255, linear in between. The
    // exact numbers matter: they are the difference between restored text with
    // clean anti-aliased edges and restored text with a visible staircase.
    var ramp = frame(32, 32, [0, 0, 0]);
    box(ramp, 5, 5, 5, 5, [0, 0, 0]);       // distance 0
    box(ramp, 7, 7, 7, 7, [10, 0, 0]);      // distance 10 - the ramp's foot
    box(ramp, 9, 9, 9, 9, [22, 0, 0]);      // distance 22 - halfway
    box(ramp, 11, 11, 11, 11, [34, 0, 0]);  // distance 34 - the ramp's top
    box(ramp, 13, 13, 13, 13, [200, 0, 0]); // well past the top
    var ra = contentAlpha(32, 32, ramp.data, backgroundProfile(32, 32, ramp.data));
    ok(ra.length === 32 * 32, 'contentAlpha returns one byte per pixel');
    eqNum(ra[5 * 32 + 5], 0, 'ramp: background is fully transparent');
    eqNum(ra[7 * 32 + 7], 0, 'ramp: distance 10 is still fully transparent');
    near(ra[9 * 32 + 9], 127.5, 1, 'ramp: distance 22 is half opaque');
    eqNum(ra[11 * 32 + 11], 255, 'ramp: distance 34 is fully opaque');
    eqNum(ra[13 * 32 + 13], 255, 'ramp: past the top stays fully opaque');

    // -- the union ------------------------------------------------------------
    // `chopped` above is the Martel failure shape: the AI kept the left half of
    // the mark and deleted the right half. Unioning the soft content alpha back
    // in restores it, and the guard then accepts what it had rejected.
    ok(judgeCandidate(flat, chopped).accept === false, 'union precondition: the raw chop is rejected');
    var u = unionWithContent(flat, chopped);
    ok(u.applied === true, 'union applies on a flat background (reason ' + u.reason + ')');
    ok(u.w === 32 && u.h === 32, 'union returns the ORIGINAL grid (print resolution)');
    ok(u.restoredPixels === 128,
      'union restores exactly the deleted half of the mark (got ' + u.restoredPixels + ')');
    var sUnion = scoreRetention(flat, u);
    ok(sUnion.destroyedPixels === 0,
      'union destroys nothing (got ' + sUnion.destroyedPixels + ')');
    ok(sUnion.destroyedFraction < scoreRetention(flat, chopped).destroyedFraction,
      'union scores materially better than the raw AI alpha');
    ok(judgeCandidate(flat, u).accept === true, 'union is ACCEPTED by the guard');
    // Colour comes from the customer's file, not from the engine's cleared pixels.
    var restoredIdx = (12 * 32 + 20) * 4;   // inside the half the AI deleted
    ok(u.data[restoredIdx + 3] === 255, 'union: the restored half is opaque');
    ok(u.data[restoredIdx] === 0 && u.data[restoredIdx + 1] === 0 && u.data[restoredIdx + 2] === 0,
      'union: restored pixels carry the ORIGINAL rgb');

    // -- THE GATE -------------------------------------------------------------
    // `photo` / `subject` above are a photographic background and a CORRECT
    // removal of its subject. The union must decline, and the counterfactual
    // below is why: applied here it would put the whole background back.
    ok(canUnion(fp) === true, 'canUnion allows a flat background');
    ok(canUnion(pp) === false, 'THE GATE: canUnion refuses a photographic background');
    ok(canUnion(null) === false, 'canUnion refuses a missing profile');
    var uPhoto = unionWithContent(photo, subject);
    ok(uPhoto.applied === false, 'THE GATE: the union is NOT applied on a photo background');
    ok(uPhoto.reason === 'photo-background', 'THE GATE: the refusal names the reason');
    ok(uPhoto.data === null, 'THE GATE: no candidate is produced to accidentally ship');

    // The counterfactual, computed by hand so deleting the gate cannot make this
    // pass. Union the same two frames anyway and background removal is undone.
    var forced = { w: 32, h: 32, data: subject.data.slice() };
    var forcedSoft = contentAlpha(32, 32, photo.data, pp);
    for (var fi = 0; fi < 32 * 32; fi++) {
      if (forcedSoft[fi] > forced.data[fi * 4 + 3]) forced.data[fi * 4 + 3] = forcedSoft[fi];
    }
    var sForced = scoreRetention(photo, forced);
    var sSubject = scoreRetention(photo, subject);
    ok(sForced.opaqueFraction > 0.95,
      'THE GATE: an ungated union restores nearly the whole frame (got '
      + sForced.opaqueFraction.toFixed(4) + ' opaque, was '
      + sSubject.opaqueFraction.toFixed(4) + ')');
    var cleared = 0, restored2 = 0;
    for (var gi = 0; gi < 32 * 32; gi++) {
      if (subject.data[gi * 4 + 3] < DESTROYED_ALPHA) {
        cleared++;
        if (forced.data[gi * 4 + 3] > OPAQUE_ALPHA) restored2++;
      }
    }
    ok(cleared > 0 && (restored2 / cleared) > 0.9,
      'THE GATE: an ungated union puts back >90% of what the removal cleared (got '
      + (restored2 / cleared).toFixed(4) + ') - background removal is UNDONE');
    // And the guard cannot save us here: it accepts the undone result, because
    // destroyedFraction is 0 by construction once everything is opaque again and
    // opaqueFraction stays just under the no-op threshold. The gate is the ONLY
    // thing standing between a photo upload and a silently disabled removal.
    ok(judgeCandidate(photo, forced).accept === true,
      'THE GATE: the guard ACCEPTS the undone removal - it cannot substitute for the gate');

    // -- degenerate input -----------------------------------------------------
    ok(scoreRetention(null, flat).valid === false, 'null original scores invalid');
    ok(scoreRetention(flat, null).valid === false, 'null candidate scores invalid');
    ok(contentMask(0, 0, new Uint8Array(0), null).count === 0, 'empty frame has no content');
    ok(contentAlpha(0, 0, new Uint8Array(0), null).length === 0, 'empty frame has no content alpha');
    ok(unionWithContent(null, flat).applied === false, 'union declines a null original');
    ok(unionWithContent(flat, null).applied === false, 'union declines a null candidate');
    ok(unionWithContent(null, flat).reason === 'unusable', 'union labels a null input unusable');

    return { passed: passed, failed: failures.length, failures: failures };
  }
  /* build:strip-end */

  var api = {
    BORDER_CONTENT_TOL: BORDER_CONTENT_TOL,
    RETENTION_REJECT: RETENTION_REJECT,
    CONTENT_RAMP_LO: CONTENT_RAMP_LO,
    CONTENT_RAMP_HI: CONTENT_RAMP_HI,
    backgroundProfile: backgroundProfile,
    contentMask: contentMask,
    contentAlpha: contentAlpha,
    scoreRetention: scoreRetention,
    judgeCandidate: judgeCandidate,
    canUnion: canUnion,
    unionWithContent: unionWithContent,
    __selfTest: __selfTest
  };

  if (typeof window !== 'undefined') window.forteeBgRetention = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})();
